import { Event } from '../types';

interface CalendarEventData {
  title: string;
  description: string;
  location: string;
  startDate: Date;
  endDate: Date;
  url?: string;
}

function formatICSDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

function sanitizeICSText(text: string): string {
  return text
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function generateICSFile(eventData: CalendarEventData): string {
  const now = new Date();
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Vybe Tribe//Event Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(eventData.startDate)}`,
    `DTEND:${formatICSDate(eventData.endDate)}`,
    `DTSTAMP:${formatICSDate(now)}`,
    `UID:${Date.now()}@vybetribe.app`,
    `SUMMARY:${sanitizeICSText(eventData.title)}`,
    `DESCRIPTION:${sanitizeICSText(eventData.description)}`,
    `LOCATION:${sanitizeICSText(eventData.location)}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    ...(eventData.url ? [`URL:${eventData.url}`] : []),
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
}

export function downloadICSFile(icsContent: string, filename: string): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

function parseEventDateTime(event: Event): Date {
  const dateStr = event.date;
  const timeStr = event.startTime;

  if (timeStr) {
    const [datePart] = dateStr.split('T');
    const combinedDateTime = `${datePart}T${timeStr}`;
    return new Date(combinedDateTime);
  }

  return new Date(dateStr);
}

export function eventToCalendarData(event: Event): CalendarEventData {
  const startDate = parseEventDateTime(event);
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

  let description = event.description;
  if (event.briefText) {
    description = `${description}\n\n${event.briefText}`;
  }
  if (event.meetingPoint) {
    description = `${description}\n\nMeeting Point: ${event.meetingPoint}`;
  }

  return {
    title: event.title,
    description: description,
    location: event.location,
    startDate: startDate,
    endDate: endDate,
    url: `${window.location.origin}/vybes/${event.id}`
  };
}

export function generateGoogleCalendarUrl(eventData: CalendarEventData): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventData.title,
    details: eventData.description,
    location: eventData.location,
    dates: `${formatICSDate(eventData.startDate).replace(/[-:]/g, '')}/${formatICSDate(eventData.endDate).replace(/[-:]/g, '')}`,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function generateOutlookUrl(eventData: CalendarEventData): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: eventData.title,
    body: eventData.description,
    location: eventData.location,
    startdt: eventData.startDate.toISOString(),
    enddt: eventData.endDate.toISOString(),
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function generateYahooCalendarUrl(eventData: CalendarEventData): string {
  const duration = Math.floor((eventData.endDate.getTime() - eventData.startDate.getTime()) / (60 * 1000));

  const params = new URLSearchParams({
    v: '60',
    title: eventData.title,
    desc: eventData.description,
    in_loc: eventData.location,
    st: formatICSDate(eventData.startDate).replace(/[-:]/g, ''),
    dur: duration.toString(),
  });

  return `https://calendar.yahoo.com/?${params.toString()}`;
}

export function exportEventToCalendar(event: Event): void {
  const calendarData = eventToCalendarData(event);
  const icsContent = generateICSFile(calendarData);
  const filename = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
  downloadICSFile(icsContent, filename);
}
